import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, ChevronRight, ArrowLeft, ArrowRight, Sparkles, Mail, X,
} from 'lucide-react';
import {
  HELP_CATEGORIES, helpArticlesOf, featuredHelpArticles, helpArticleById, searchHelpArticles,
  orientationArticles, type HelpArticle, type HelpCategoryId,
} from '@palacete/shared/help-articles';
import { coreNavItems } from '../../lib/panelNavigation';
import { useSupportConfig } from '../../lib/useSupportConfig';
import type { HelpView } from '../../lib/useHelp';
import HelpAssistant from './HelpAssistant';
import SupportContactForm from './SupportContactForm';

// Cuerpo del centro de ayuda (GOL-11). Un solo componente para las dos
// superficies —el drawer del header y la página `/ayuda`— porque son la misma
// tarea con distinto ancho: duplicarlas garantizaría que una se quedara atrás.

type Props = {
  variant: 'drawer' | 'page';
  view: HelpView;
  onViewChange: (view: HelpView) => void;
  /** El drawer se cierra al navegar; la página no tiene nada que cerrar. */
  onNavigate?: () => void;
};

// La ruta del centro de ayuda sale del catálogo de navegación, no de un string
// suelto: es la misma convención que el resto de accesos del panel.
const HELP_ROUTE = coreNavItems.find((item) => item.id === 'help')?.to ?? '/ayuda';

export default function HelpCenter({ variant, view, onViewChange, onNavigate }: Props) {
  const navigate = useNavigate();
  const support = useSupportConfig();
  const [query, setQuery] = useState('');
  // La página abre en la primera categoría del catálogo —hoy «Primeros pasos»—
  // en vez de en una fija: el orden de `HELP_CATEGORIES` es la decisión editorial.
  const [category, setCategory] = useState<HelpCategoryId>(HELP_CATEGORIES[0].id);

  const results = useMemo(() => (query.trim().length > 1 ? searchHelpArticles(query) : null), [query]);

  function goTo(to: string) {
    onNavigate?.();
    navigate(to);
  }

  function openArticle(id: string) {
    onViewChange({ kind: 'article', id });
  }

  const escalationButtons = (
    <div className="flex flex-wrap gap-2">
      {support?.assistantEnabled && (
        <button
          type="button"
          onClick={() => onViewChange({ kind: 'assistant', question: query.trim() || undefined })}
          className="inline-flex items-center gap-2 rounded-md bg-ui-accent px-3 py-2 text-sm font-medium text-ui-text-inverse transition-colors hover:bg-ui-accent-hover"
        >
          <Sparkles size={15} />
          Preguntarle al asistente
        </button>
      )}
      <button
        type="button"
        onClick={() => onViewChange({ kind: 'contact' })}
        className="inline-flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 text-sm font-medium text-ui-text transition-colors hover:border-ui-accent"
      >
        <Mail size={15} />
        Escribir a soporte
      </button>
    </div>
  );

  if (view.kind === 'assistant') {
    return (
      <HelpAssistant
        initialQuestion={view.question}
        onBack={() => onViewChange({ kind: 'index' })}
        onContact={() => onViewChange({ kind: 'contact' })}
        onOpenArticle={openArticle}
        onNavigate={goTo}
      />
    );
  }

  if (view.kind === 'contact') {
    return <SupportContactForm onBack={() => onViewChange({ kind: 'index' })} />;
  }

  if (view.kind === 'article') {
    const article = helpArticleById(view.id);
    if (!article) return null;
    return (
      <ArticleView
        article={article}
        onBack={() => onViewChange({ kind: 'index' })}
        onGoTo={goTo}
        escalation={escalationButtons}
      />
    );
  }

  const searchField = (
    <div className="relative">
      <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ui-text-subtle" />
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Busca por lo que quieres hacer…"
        className="w-full rounded-md border border-ui-border bg-ui-bg py-2 pl-9 pr-9 text-sm text-ui-text placeholder:text-ui-text-subtle focus:border-ui-accent focus:outline-none"
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery('')}
          aria-label="Limpiar búsqueda"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ui-text-subtle transition-colors hover:text-ui-text"
        >
          <X size={15} />
        </button>
      )}
    </div>
  );

  // ── Resultados de búsqueda (misma forma en drawer y página) ───────────────
  if (results) {
    return (
      <div className="space-y-4">
        {searchField}
        {results.length > 0 ? (
          <>
            <p className="text-sm text-ui-text-subtle">
              {results.length === 1 ? '1 resultado' : `${results.length} resultados`}
            </p>
            <ArticleList articles={results} onOpen={openArticle} />
          </>
        ) : (
          <div className="space-y-3 rounded-lg border border-ui-border bg-ui-surface px-4 py-4">
            <p className="text-sm text-ui-text">No encontramos nada para «{query.trim()}».</p>
            <p className="text-sm text-ui-text-muted">
              {support?.assistantEnabled
                ? 'Pregúntaselo al asistente: entiende preguntas escritas como se te ocurran.'
                : 'Escríbenos y te ayudamos con eso.'}
            </p>
            {escalationButtons}
          </div>
        )}
      </div>
    );
  }

  // ── Índice ────────────────────────────────────────────────────────────────
  const orientation = orientationArticles();
  const orientationIds = new Set(orientation.map((a) => a.id));
  // Lo destacado que ya salió arriba no se repite tres renglones más abajo.
  const featured = featuredHelpArticles().filter((a) => !orientationIds.has(a.id));

  if (variant === 'drawer') {
    return (
      <div className="space-y-5">
        {/* La orientación va antes del buscador, y sin condicionarla al estado del
            tenant: buscar exige saber qué palabra escribir, y quien llega perdido
            todavía no la tiene. Quien ya sabe, sigue teniendo el campo a un renglón. */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ui-text-subtle">Si es tu primera vez</h3>
          <ArticleList articles={orientation} onOpen={openArticle} />
        </section>

        {searchField}

        <section className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ui-text-subtle">Lo más buscado</h3>
          <ArticleList articles={featured} onOpen={openArticle} />
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ui-text-subtle">Por tema</h3>
          {HELP_CATEGORIES.filter((cat) => !orientation.some((a) => a.category === cat.id)).map((cat) => (
            <details key={cat.id} className="rounded-md border border-ui-border bg-ui-surface">
              <summary className="cursor-pointer list-none px-3 py-2 text-sm text-ui-text marker:content-none [&::-webkit-details-marker]:hidden">
                {cat.label}
              </summary>
              <div className="border-t border-ui-border px-1 py-1">
                <ArticleList articles={helpArticlesOf(cat.id)} onOpen={openArticle} bare />
              </div>
            </details>
          ))}
        </section>

        <section className="space-y-3 border-t border-ui-border pt-4">
          <p className="text-sm text-ui-text-muted">¿No encuentras lo que buscas?</p>
          {escalationButtons}
          <button
            type="button"
            onClick={() => goTo(HELP_ROUTE)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-ui-accent hover:underline"
          >
            Ver todo el centro de ayuda
            <ArrowRight size={14} />
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {searchField}

      <div className="grid gap-4 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
        <nav className="space-y-1">
          {HELP_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setCategory(cat.id)}
              className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                cat.id === category
                  ? 'border border-ui-accent/30 bg-ui-accent/15 text-ui-text'
                  : 'text-ui-text-muted hover:bg-ui-text/[0.06] hover:text-ui-text'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </nav>

        <section className="min-w-0 space-y-3">
          <div>
            <h2 className="text-base font-semibold text-ui-text">
              {HELP_CATEGORIES.find((c) => c.id === category)?.label}
            </h2>
            <p className="text-sm text-ui-text-muted">
              {HELP_CATEGORIES.find((c) => c.id === category)?.description}
            </p>
          </div>
          <ArticleList articles={helpArticlesOf(category)} onOpen={openArticle} />
        </section>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {support?.assistantEnabled && (
          <div className="space-y-2 rounded-xl border border-ui-border bg-ui-surface p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-ui-text">
              <Sparkles size={15} className="text-ui-accent" />
              Pregúntale al asistente
            </p>
            <p className="text-sm text-ui-text-muted">Responde dudas sobre tu panel al momento.</p>
            <button
              type="button"
              onClick={() => onViewChange({ kind: 'assistant' })}
              className="inline-flex items-center gap-2 rounded-md bg-ui-accent px-3 py-2 text-sm font-medium text-ui-text-inverse transition-colors hover:bg-ui-accent-hover"
            >
              Abrir asistente
            </button>
          </div>
        )}
        <div className="space-y-2 rounded-xl border border-ui-border bg-ui-surface p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-ui-text">
            <Mail size={15} className="text-ui-accent" />
            ¿Necesitas a una persona?
          </p>
          <p className="text-sm text-ui-text-muted">
            {support?.promise ?? 'Escríbenos y te contestamos.'}
          </p>
          <button
            type="button"
            onClick={() => onViewChange({ kind: 'contact' })}
            className="inline-flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 text-sm font-medium text-ui-text transition-colors hover:border-ui-accent"
          >
            Escribir a soporte
          </button>
        </div>
      </div>
    </div>
  );
}

function ArticleList({
  articles, onOpen, bare = false,
}: {
  articles: HelpArticle[];
  onOpen: (id: string) => void;
  bare?: boolean;
}) {
  return (
    <ul className={bare ? '' : 'space-y-1'}>
      {articles.map((article) => (
        <li key={article.id}>
          <button
            type="button"
            onClick={() => onOpen(article.id)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-ui-text-muted transition-colors hover:bg-ui-text/[0.06] hover:text-ui-text"
          >
            <ChevronRight size={14} className="shrink-0 text-ui-text-subtle" />
            <span className="min-w-0">{article.question}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ArticleView({
  article, onBack, onGoTo, escalation,
}: {
  article: HelpArticle;
  onBack: () => void;
  onGoTo: (to: string) => void;
  escalation: React.ReactNode;
}) {
  const [helpful, setHelpful] = useState<'yes' | 'no' | null>(null);

  return (
    <motion.div
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.16 }}
      className="space-y-4"
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-ui-text-muted transition-colors hover:text-ui-text"
      >
        <ArrowLeft size={14} />
        Volver
      </button>

      <h2 className="text-base font-semibold text-ui-text">{article.question}</h2>

      <div className="space-y-3">
        {article.body.map((paragraph) => (
          <p key={paragraph.slice(0, 24)} className="text-sm leading-relaxed text-ui-text-muted">
            {paragraph}
          </p>
        ))}
      </div>

      {article.to && (
        <button
          type="button"
          onClick={() => onGoTo(article.to as string)}
          className="inline-flex items-center gap-2 rounded-md bg-ui-accent px-3 py-2 text-sm font-medium text-ui-text-inverse transition-colors hover:bg-ui-accent-hover"
        >
          {article.toLabel ?? 'Ir ahí'}
          <ArrowRight size={14} />
        </button>
      )}

      <div className="space-y-3 border-t border-ui-border pt-4">
        {helpful === 'yes' ? (
          <p className="text-sm text-ui-success">Qué bueno. Aquí seguimos si necesitas algo más.</p>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-ui-text-muted">¿Te sirvió?</span>
            <button
              type="button"
              onClick={() => setHelpful('yes')}
              className="rounded-md border border-ui-border px-2.5 py-1 text-sm text-ui-text transition-colors hover:border-ui-accent"
            >
              Sí
            </button>
            <button
              type="button"
              onClick={() => setHelpful('no')}
              className="rounded-md border border-ui-border px-2.5 py-1 text-sm text-ui-text transition-colors hover:border-ui-accent"
            >
              No
            </button>
          </div>
        )}
        {helpful === 'no' && (
          <div className="space-y-2">
            <p className="text-sm text-ui-text-muted">Vamos por otro camino:</p>
            {escalation}
          </div>
        )}
      </div>
    </motion.div>
  );
}
